import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import { ServiceIcon, DEFAULT_ICON } from '../constants/serviceIcons';
import { localizedPath } from '../utils/routes';
import PromotionBanner from '../components/PromotionBanner';
import SEOHead from '../components/SEOHead';

const API = process.env.REACT_APP_API_URL || '/api';

export default function Home() {
  const { t, language } = useLanguage();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const { mc } = useMarketingCopy();
  const [services, setServices] = useState([]);
  const [visible, setVisible] = useState(false);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleRating, setGoogleRating] = useState(null);
  const [googleTotal, setGoogleTotal] = useState(0);
  const [googlePlaceId, setGooglePlaceId] = useState(null);

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    axios.get(`${API}/services?lang=${language}`).then(r => setServices((Array.isArray(r.data) ? r.data : []).slice(0, 6))).catch(err => console.error('Home services fetch failed:', err.message));
    axios.get(`${API}/google-reviews`).then(r => {
      if (r.data.configured && r.data.reviews.length > 0) {
        setGoogleReviews(r.data.reviews);
        setGoogleRating(r.data.rating);
        setGoogleTotal(r.data.totalReviews);
        setGooglePlaceId(r.data.placeId);
      }
    }).catch(() => {});
  }, [language]);

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh' }}>
      <SEOHead page="home" />

      <PromotionBanner placement="home_hero" />

      {/* HERO */}
      <section style={{
        position: 'relative', height: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', overflow: 'hidden', flexDirection: 'column',
      }}>
        {/* Hero background image */}
        <img
          src="/images/hero/hero-garage-atelier.jpg"
          alt={t.images?.hero || ''}
          width={1920}
          height={1080}
          fetchpriority="high"
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', opacity: 0.35, filter: 'brightness(0.6)', zIndex: 0,
          }}
          onError={e => { e.target.style.display = 'none'; }}
        />
        {/* Background gradient */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 20% 50%, rgba(148,163,184,0.06) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(139,115,85,0.04) 0%, transparent 50%), #0C1220',
        }} />
        {/* Decorative lines */}
        <div style={{ position: 'absolute', left: '5%', top: 0, bottom: 0, width: '1px', background: 'linear-gradient(to bottom, transparent, rgba(148,163,184,0.2), transparent)' }} />
        <div style={{ position: 'absolute', right: '5%', top: 0, bottom: 0, width: '1px', background: 'linear-gradient(to bottom, transparent, rgba(148,163,184,0.2), transparent)' }} />

        <div style={{
          position: 'relative', textAlign: 'center', zIndex: 2, padding: '0 2rem',
          opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1)',
        }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: '#D42B2B', textTransform: 'uppercase', marginBottom: '2rem', fontFamily: "'Barlow', sans-serif" }}>
            ——&nbsp;&nbsp; {mc('home', 'hero.subtitle', t.home.hero.subtitle)} &nbsp;&nbsp;——
          </div>
          <h1 style={{
            fontFamily: "'Rajdhani', sans-serif", fontWeight: 300,
            fontSize: 'clamp(3.5rem, 9vw, 8rem)', color: '#E8ECF0', lineHeight: 0.9,
            letterSpacing: '-0.02em', marginBottom: '1.5rem',
          }}>
            Mécanique<br /><span style={{ color: '#D42B2B', fontStyle: 'italic' }}>Auto CJPA</span>
          </h1>
          <p style={{
            fontFamily: "'Barlow', sans-serif", fontWeight: 300, fontSize: '0.85rem',
            letterSpacing: '0.15em', color: '#94A3B8', maxWidth: '500px', margin: '0 auto 3rem',
            lineHeight: 1.8, textTransform: 'uppercase',
          }}>
            {mc('home', 'hero.description', t.home.hero.description)}
          </p>
          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to={lp('book')} style={{
              background: '#D42B2B', color: '#0C1220', padding: '1rem 2.5rem',
              textDecoration: 'none', fontFamily: "'Barlow', sans-serif",
              fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              fontWeight: 600, transition: 'all 0.3s',
              boxShadow: '0 0 30px rgba(148,163,184,0.3)',
            }}>{mc('home', 'hero.bookButton', t.home.hero.bookButton)}</Link>
            <Link to={lp('services')} style={{
              background: 'transparent', color: '#D42B2B', padding: '1rem 2.5rem',
              textDecoration: 'none', fontFamily: "'Barlow', sans-serif",
              fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              border: '1px solid rgba(148,163,184,0.4)', transition: 'all 0.3s',
            }}>{mc('home', 'hero.servicesButton', t.home.hero.servicesButton)}</Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '1px', height: '60px', background: 'linear-gradient(to bottom, transparent, #D42B2B)', animation: 'pulse 2s infinite' }} />
        </div>
      </section>

      {/* STATS BAR */}
      <section style={{ background: 'rgba(148,163,184,0.05)', borderTop: '1px solid rgba(148,163,184,0.15)', borderBottom: '1px solid rgba(148,163,184,0.15)', padding: 'clamp(1.5rem, 3vw, 2.5rem) clamp(1rem, 3vw, 3rem)' }}>
        <div className="cjpa-stats-grid" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {[[mc('home', 'stats.years', t.home.stats.years), mc('home', 'stats.yearsLabel', t.home.stats.yearsLabel)], [mc('home', 'stats.vehicles', t.home.stats.vehicles), mc('home', 'stats.vehiclesLabel', t.home.stats.vehiclesLabel)], [mc('home', 'stats.satisfaction', t.home.stats.satisfaction), mc('home', 'stats.satisfactionLabel', t.home.stats.satisfactionLabel)], [mc('home', 'stats.technicians', t.home.stats.technicians), mc('home', 'stats.techniciansLabel', t.home.stats.techniciansLabel)]].map(([num, label]) => (
            <div key={label}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', color: '#D42B2B', fontWeight: 300 }}>{num}</div>
              <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', color: '#94A3B8', textTransform: 'uppercase', marginTop: '0.3rem' }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section style={{ padding: 'clamp(3rem, 6vw, 7rem) clamp(1rem, 3vw, 3rem)', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 5rem)' }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '1rem' }}>{mc('home', 'services.subtitle', t.home.services.subtitle)}</div>
          <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#E8ECF0', fontWeight: 300, letterSpacing: '-0.01em' }}>
            {t.home.services.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.home.services.titleHighlight}</span>
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '1.5px', background: 'rgba(148,163,184,0.1)' }}>
          {services.map((s) => (
            <div key={s.id} style={{
              background: '#111827', padding: '2.5rem',
              display: 'flex', flexDirection: 'column',
              transition: 'background 0.3s',
              cursor: 'default',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#111827'}
              onMouseLeave={e => e.currentTarget.style.background = '#111827'}
            >
              <div style={{ marginBottom: '1rem' }}><ServiceIcon iconKey={s.image_url || DEFAULT_ICON} size={32} /></div>
              <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{s.category}</div>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.4rem', color: '#E8ECF0', marginBottom: '0.8rem', fontWeight: 500 }}>{s.name}</h3>
              <p style={{ color: '#94A3B8', fontSize: '0.8rem', lineHeight: 1.7, marginBottom: '0' }}>{s.description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(148,163,184,0.1)', paddingTop: '1rem', marginTop: 'auto' }}>
                <span style={{ color: '#D42B2B', fontSize: '0.85rem', fontFamily: "'Rajdhani', sans-serif" }}>
                  {s.price_min ? `${t.home.services.from} $${s.price_min}` : t.services.callForPricing}
                </span>
                <span style={{ color: '#64748B', fontSize: '0.7rem', letterSpacing: '0.1em' }}>{s.duration_minutes} {t.home.services.min}</span>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <Link to={lp('services')} style={{
            color: '#D42B2B', textDecoration: 'none', fontFamily: "'Barlow', sans-serif",
            fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase',
            borderBottom: '1px solid rgba(148,163,184,0.4)', paddingBottom: '3px',
          }}>{t.home.services.viewAll}</Link>
        </div>
      </section>

      <PromotionBanner placement="home_inline" />

      {/* WHY CHOOSE US */}
      <section style={{ background: 'rgba(148,163,184,0.03)', border: 'none', borderTop: '1px solid rgba(148,163,184,0.08)', borderBottom: '1px solid rgba(148,163,184,0.08)', padding: 'clamp(3rem, 6vw, 7rem) clamp(1rem, 3vw, 3rem)' }}>
        <div className="cjpa-why-grid" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div>
            <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '1rem' }}>{mc('home', 'whyChoose.subtitle', t.home.whyChoose.subtitle)}</div>
            <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(2rem, 4vw, 3.5rem)', color: '#E8ECF0', fontWeight: 300, lineHeight: 1.1, marginBottom: '2rem' }}>
              {t.home.whyChoose.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.home.whyChoose.titleHighlight}</span><br />{t.home.whyChoose.titleEnd}
            </h2>
            <div style={{ overflow: 'hidden', borderLeft: '4px solid #D42B2B', marginBottom: '2rem' }}>
              <img
                src="/images/about/workshop-interior.jpg"
                alt={t.images?.aboutShop || ''}
                width={800}
                height={200}
                loading="lazy"
                style={{ width: '100%', height: '200px', objectFit: 'cover', filter: 'brightness(0.7)', display: 'block' }}
                onError={e => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
            <p style={{ color: '#94A3B8', fontSize: '0.85rem', lineHeight: 1.9, marginBottom: '2rem' }}>
              {mc('home', 'whyChoose.description', t.home.whyChoose.description)}
            </p>
            <Link to={lp('book')} style={{
              display: 'inline-block', background: 'transparent', color: '#D42B2B',
              padding: '0.9rem 2rem', textDecoration: 'none',
              fontFamily: "'Barlow', sans-serif", fontSize: '0.65rem',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              border: '1px solid rgba(148,163,184,0.5)',
            }}>{mc('home', 'whyChoose.button', t.home.whyChoose.button)}</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {[
              [mc('home', 'whyChoose.certified.title', t.home.whyChoose.certified.title), mc('home', 'whyChoose.certified.desc', t.home.whyChoose.certified.desc)],
              [mc('home', 'whyChoose.pricing.title', t.home.whyChoose.pricing.title), mc('home', 'whyChoose.pricing.desc', t.home.whyChoose.pricing.desc)],
              [mc('home', 'whyChoose.parts.title', t.home.whyChoose.parts.title), mc('home', 'whyChoose.parts.desc', t.home.whyChoose.parts.desc)],
              [mc('home', 'whyChoose.inspection.title', t.home.whyChoose.inspection.title), mc('home', 'whyChoose.inspection.desc', t.home.whyChoose.inspection.desc)],
            ].map(([title, desc]) => (
              <div key={title} style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start' }}>
                <div style={{ width: '1px', background: '#D42B2B', flexShrink: 0, marginTop: '6px', height: '40px' }} />
                <div>
                  <div style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", fontSize: '1.1rem', marginBottom: '0.3rem' }}>{title}</div>
                  <div style={{ color: '#94A3B8', fontSize: '0.78rem', lineHeight: 1.7 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      {googleReviews.length > 0 && (
        <section style={{ padding: 'clamp(3rem, 6vw, 7rem) clamp(1rem, 3vw, 3rem)', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 5rem)' }}>
            <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '1rem' }}>{mc('home', 'reviews.subtitle', t.home.reviews.subtitle)}</div>
            <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#E8ECF0', fontWeight: 300, letterSpacing: '-0.01em' }}>
              {t.home.reviews.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.home.reviews.titleHighlight}</span>
            </h2>
            {googleRating && (
              <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.8rem' }}>
                <div style={{ display: 'flex', gap: '2px' }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <span key={i} style={{ color: i <= Math.round(googleRating) ? '#D42B2B' : '#1a2332', fontSize: '1.2rem' }}>&#9733;</span>
                  ))}
                </div>
                <span style={{ color: '#E8ECF0', fontFamily: "'Rajdhani', sans-serif", fontSize: '1.3rem', fontWeight: 500 }}>{googleRating}</span>
                <span style={{ color: '#94A3B8', fontSize: '0.75rem', letterSpacing: '0.1em' }}>({googleTotal} {t.home.reviews.reviews})</span>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '1.5px', background: 'rgba(148,163,184,0.1)' }}>
            {googleReviews.map((review, idx) => (
              <div key={idx} style={{
                background: '#111827', padding: '2rem',
                transition: 'background 0.3s',
                display: 'flex', flexDirection: 'column', gap: '1rem',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                onMouseLeave={e => e.currentTarget.style.background = '#111827'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  {review.authorPhoto ? (
                    <img
                      src={review.authorPhoto}
                      alt={review.authorName}
                      style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      background: 'rgba(148,163,184,0.15)', color: '#D42B2B',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: "'Rajdhani', sans-serif", fontSize: '1rem', fontWeight: 600,
                    }}>
                      {review.authorName.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div style={{ color: '#E8ECF0', fontSize: '0.85rem', fontFamily: "'Rajdhani', sans-serif", fontWeight: 500 }}>{review.authorName}</div>
                    <div style={{ color: '#94A3B8', fontSize: '0.65rem', letterSpacing: '0.05em' }}>{review.relativeTime}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '2px' }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <span key={i} style={{ color: i <= review.rating ? '#D42B2B' : '#1a2332', fontSize: '0.85rem' }}>&#9733;</span>
                  ))}
                </div>
                <p style={{ color: '#94A3B8', fontSize: '0.8rem', lineHeight: 1.7, margin: 0, flex: 1 }}>
                  {review.text.length > 200 ? review.text.slice(0, 200) + '...' : review.text}
                </p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <a
              href={`https://search.google.com/local/reviews?placeid=${googlePlaceId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#D42B2B', textDecoration: 'none', fontFamily: "'Barlow', sans-serif",
                fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                borderBottom: '1px solid rgba(148,163,184,0.4)', paddingBottom: '3px',
              }}
            >{t.home.reviews.viewAll}</a>
          </div>
        </section>
      )}

      {/* CTA */}
      <section style={{ padding: 'clamp(3rem, 7vw, 8rem) clamp(1rem, 3vw, 3rem)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(148,163,184,0.05) 0%, transparent 70%)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '1rem' }}>{mc('home', 'cta.subtitle', t.home.cta.subtitle)}</div>
          <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: '#E8ECF0', fontWeight: 300, marginBottom: '1.5rem' }}>
            {t.home.cta.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.home.cta.titleHighlight}</span>
          </h2>
          <p style={{ color: '#94A3B8', fontSize: '0.85rem', letterSpacing: '0.1em', marginBottom: '3rem', maxWidth: '450px', margin: '0 auto 3rem' }}>
            {mc('home', 'cta.description', t.home.cta.description)}
          </p>
          <Link to={lp('book')} style={{
            display: 'inline-block', background: '#D42B2B', color: '#0C1220',
            padding: '1.2rem 3.5rem', textDecoration: 'none',
            fontFamily: "'Barlow', sans-serif", fontSize: '0.7rem',
            letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600,
            boxShadow: '0 0 50px rgba(148,163,184,0.25)',
          }}>{mc('home', 'cta.button', t.home.cta.button)}</Link>
        </div>
      </section>

      <style>{`
        @keyframes pulse { 0%,100% { opacity:0.4 } 50% { opacity:1 } }
        a:hover { opacity: 0.85; }
      `}</style>
    </div>
  );
}
